import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Upload, FolderOpen, Crop, Settings as SettingsIcon, Trash2, X,
  Square, Monitor, Tablet, Smartphone, Copy as CopyIcon, Link as LinkIcon,
  ImageOff,
} from 'lucide-react';
import { SHAPES, SHAPE_KEYS, DISPLAY_MODES, getDeviceFrame, writeDeviceFrame, writeDeviceFrameWhole, desktopAsFlat, isResponsiveFrame } from './shapes';
import { useBuilderDevice } from './DeviceContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * FloatingImageToolbar — Iter 151.
 *
 * Renders next to a double-clicked image. Six primary actions:
 *   🖼 Replace   — file picker (re-uses media upload endpoint)
 *   📚 Library   — opens the existing global media picker
 *   ✂ Crop      — opens zoom + offset sliders (Phase 1: lightweight)
 *   ⬜ Frame    — opens the 14-shape mask picker
 *   ⚙ Settings  — Fill/Fit/Stretch, radius, opacity, border width/color
 *   🗑 Remove   — clears the underlying image_url
 *
 * Positions itself just below the image, clamped to the viewport.
 * Closes on Escape, outside-click, or X.
 */
const Btn = ({ icon: Icon, label, onClick, testid, danger, inline }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    data-testid={testid}
    className={`flex flex-col items-center gap-1 px-2.5 py-2 rounded-lg text-[10.5px] font-medium uppercase tracking-wider transition-all min-w-[64px] ${
      danger
        ? (inline ? 'text-red-300 hover:bg-red-500/15' : 'text-red-600 hover:bg-red-50')
        : (inline ? 'text-white/80 hover:bg-white/[0.06] hover:text-white' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900')
    }`}
  >
    <Icon size={14} />
    <span>{label}</span>
  </button>
);

export default function FloatingImageToolbar({
  blockId,
  field,
  rect,
  imageUrl,
  frame: rawFrame,
  onUpdateFrame: writeRawFrame,
  onReplace,
  onMediaPick,
  onRemove,
  onClose,
  inline = false,    // Iter 178h — when true, render as an in-panel section
                     // (no fixed positioning, no X button, no outside-click handler).
}) {
  const { device, setDevice } = useBuilderDevice();
  const [panel, setPanel] = useState(null); // 'frame' | 'settings' | 'crop'
  const [cropSnapshot, setCropSnapshot] = useState(null); // frame value when Crop opened
  const [dragRect, setDragRect] = useState(null); // live image bounds for the drag overlay
  const fileRef = useRef(null);
  const cardRef = useRef(null);
  const dragStateRef = useRef(null);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  // ── Phase 4 — per-device frame resolution ──
  // `rawFrame` is what's stored on the block (flat OR responsive).
  // `frame` is the flat view of the CURRENT device for the toolbar's UI.
  const frame = getDeviceFrame(rawFrame, device);
  const responsive = isResponsiveFrame(rawFrame);
  const onUpdateFrame = (next) => {
    // The Apply/Cancel layer passes the whole flat frame. Promote to
    // responsive storage and write only the active device's slot.
    writeRawFrame(writeDeviceFrameWhole(rawFrame, device, next));
  };
  const updateField = (k, v) => {
    writeRawFrame(writeDeviceFrame(rawFrame, device, k, v));
  };
  const syncFromDesktop = () => {
    if (device === 'desktop') {
      // Re-broadcast desktop config to all devices.
      const flat = desktopAsFlat(rawFrame);
      writeRawFrame({ _responsive: true, desktop: { ...flat }, tablet: { ...flat }, mobile: { ...flat } });
      toast.success('Synced desktop settings to all devices');
    } else {
      const flat = desktopAsFlat(rawFrame);
      writeRawFrame(writeDeviceFrameWhole(rawFrame, device, flat));
      toast.success(`Synced desktop settings to ${device}`);
    }
  };

  // Outside-click / Escape close.
  useEffect(() => {
    if (inline) return; // Iter 178h — inline mode lives inside the EditDrawer; no outside-click close.
    const onDoc = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        // Drag overlay clicks shouldn't close the toolbar.
        if (e.target.closest && e.target.closest('[data-testid="image-crop-drag-overlay"]')) return;
        onClose?.();
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, inline]);

  // Track the image's live screen bounds so the drag overlay sticks to it
  // across scroll/resize while the Crop panel is open.
  useEffect(() => {
    if (panel !== 'crop') { setDragRect(null); return; }
    const recompute = () => {
      const el = document.querySelector(`[data-wb-image="${blockId}/${field}"]`);
      if (!el) { setDragRect(null); return; }
      const r = el.getBoundingClientRect();
      setDragRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    recompute();
    window.addEventListener('scroll', recompute, true);
    window.addEventListener('resize', recompute);
    return () => {
      window.removeEventListener('scroll', recompute, true);
      window.removeEventListener('resize', recompute);
    };
  }, [panel, blockId, field, frame?.shape, frame?.scale, frame?.borderWidth]);

  // Snapshot the frame the first time Crop opens, and clear it when Crop closes.
  useEffect(() => {
    if (panel === 'crop' && cropSnapshot === null) {
      setCropSnapshot({ ...(frame || {}) });
    } else if (panel !== 'crop' && cropSnapshot !== null) {
      setCropSnapshot(null);
    }
  }, [panel, frame, cropSnapshot]);

  // Phase 4 — Closing the Crop panel on device change avoids the
  // edge-case where a snapshot taken on Device A would be reverted to
  // Device B. User must re-enter Crop after switching devices.
  const prevDeviceRef = useRef(device);
  useEffect(() => {
    if (prevDeviceRef.current !== device) {
      prevDeviceRef.current = device;
      setPanel((p) => (p === 'crop' ? null : p));
    }
  }, [device]);

  if (!inline && !rect) return null;
  // Flip-up logic: if there isn't enough room below the image for the
  // tallest panel (Crop ≈ 320px with rotation+apply/cancel), open above.
  const PANEL_HEIGHT = 320;
  const spaceBelow = rect ? window.innerHeight - rect.bottom : 0;
  const flipUp = rect ? (spaceBelow < PANEL_HEIGHT && rect.top > PANEL_HEIGHT) : false;
  const top  = !rect ? 0 : (flipUp
    ? Math.max(8, rect.top - PANEL_HEIGHT - 10)
    : Math.max(8, Math.min(window.innerHeight - PANEL_HEIGHT - 16, rect.bottom + 10)));
  const left = !rect ? 0 : Math.max(8, Math.min(Math.max(8, window.innerWidth - 470), rect.left));

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/media/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onReplace(res.data.url);
      toast.success('Image replaced');
    } catch { toast.error('Upload failed'); }
  };

  // ── Drag-to-reposition (Canva-style) ──────────────────────────
  const onDragStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: (frame?.offsetX) || 0,
      startOffsetY: (frame?.offsetY) || 0,
    };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragEnd, { once: true });
  };
  const onDragMove = (e) => {
    const s = dragStateRef.current;
    if (!s) return;
    onUpdateFrame({
      ...(frame || {}),
      offsetX: Math.max(-400, Math.min(400, s.startOffsetX + (e.clientX - s.startX))),
      offsetY: Math.max(-400, Math.min(400, s.startOffsetY + (e.clientY - s.startY))),
    });
  };
  const onDragEnd = () => {
    dragStateRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
  };

  const applyCrop = () => { setCropSnapshot(null); setPanel(null); };
  const cancelCrop = () => {
    // Revert ONLY the crop-related fields (scale, offsets, rotation) so
    // the user's shape/displayMode/border choices made outside Crop
    // mode are preserved.
    if (cropSnapshot) {
      onUpdateFrame({
        ...(frame || {}),
        scale: cropSnapshot.scale || 1,
        offsetX: cropSnapshot.offsetX || 0,
        offsetY: cropSnapshot.offsetY || 0,
        rotation: cropSnapshot.rotation || 0,
      });
    }
    setCropSnapshot(null);
    setPanel(null);
  };
  const resetCrop = () => onUpdateFrame({ ...(frame || {}), scale: 1, offsetX: 0, offsetY: 0, rotation: 0 });

  return (
    <>
    <div
      ref={cardRef}
      className={inline
        ? "rounded-lg p-1.5"
        : "fixed z-[160] bg-white rounded-xl border border-gray-200 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.32)] p-1.5"
      }
      style={inline
        ? { background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(167,139,250,0.18)' }
        : { top, left, maxWidth: 460 }
      }
      data-testid="image-toolbar"
      data-wb-overlay="1"
      data-wb-inline={inline ? '1' : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Iter 189j — Dark violet theme overrides for the inline variant.
          Rewrites the panel chrome (borders, text, buttons, sliders) so the
          IMAGE toolbar blends into the dark-purple Edit Drawer instead of
          appearing as a white box. Scoped by [data-wb-inline="1"] so the
          FLOATING variant (when used on public embeds) keeps its light
          theme unchanged. */}
      {inline && (
        <style>{`
          [data-wb-inline="1"] .border-gray-100 { border-color: rgba(255,255,255,0.08) !important; }
          [data-wb-inline="1"] .border-gray-200 { border-color: rgba(255,255,255,0.12) !important; }
          [data-wb-inline="1"] .border-t.border-gray-100 { border-top-color: rgba(255,255,255,0.08) !important; }
          [data-wb-inline="1"] .text-gray-400 { color: rgba(255,255,255,0.45) !important; }
          [data-wb-inline="1"] .text-gray-500 { color: rgba(255,255,255,0.55) !important; }
          [data-wb-inline="1"] .text-gray-700 { color: rgba(255,255,255,0.85) !important; }
          [data-wb-inline="1"] .bg-gray-50 { background-color: rgba(255,255,255,0.04) !important; }
          [data-wb-inline="1"] .bg-gray-100 { background-color: rgba(255,255,255,0.06) !important; }
          [data-wb-inline="1"] .bg-gray-200 { background-color: rgba(255,255,255,0.10) !important; }
          [data-wb-inline="1"] .hover\\:bg-gray-100:hover { background-color: rgba(255,255,255,0.08) !important; }
          [data-wb-inline="1"] .hover\\:bg-gray-200:hover { background-color: rgba(255,255,255,0.14) !important; }
          [data-wb-inline="1"] .hover\\:border-gray-200:hover { border-color: rgba(255,255,255,0.20) !important; }
          [data-wb-inline="1"] .hover\\:text-gray-700:hover { color: rgba(255,255,255,0.9) !important; }
          [data-wb-inline="1"] .hover\\:text-gray-900:hover { color: rgba(255,255,255,1) !important; }
          [data-wb-inline="1"] .text-\\[\\#5B21B6\\] { color: #DDD6FE !important; }
          [data-wb-inline="1"] .bg-\\[\\#8B5CF6\\]\\/10 { background-color: rgba(139,92,246,0.20) !important; }
          [data-wb-inline="1"] .border-\\[\\#8B5CF6\\]\\/30 { border-color: rgba(167,139,250,0.4) !important; }
          [data-wb-inline="1"] input[type="color"] { background: transparent; border-color: rgba(255,255,255,0.15) !important; }
        `}</style>
      )}
      {/* Iter 190i — Preview thumbnail + link input.
          Shown in inline mode (Edit Drawer) so vendors can SEE which image
          they're editing before clicking Replace / Remove. Previously the
          toolbar jumped straight into action buttons — with no visible
          reference to the current photo, users on the Team section
          (freshly-added, empty photos) couldn't tell which slot they had
          selected. The preview + link input close that loop and expose
          the 3 primary actions the user asked for: Replace, Remove, Add
          Link — all with a clear thumbnail of the target photo. */}
      {inline && (
        <div className="mb-2">
          <div
            className="w-full aspect-video rounded-lg overflow-hidden bg-white/[0.04] border border-white/10 flex items-center justify-center"
            data-testid="image-toolbar-preview"
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="flex flex-col items-center gap-1.5 text-white/50">
                <ImageOff size={22} />
                <span className="text-[10px] uppercase tracking-[0.16em] font-bold">No image yet</span>
              </div>
            )}
          </div>
          <p className="mt-1.5 text-[10px] text-white/45 leading-snug">
            {imageUrl ? 'Current image · click Replace or Library to swap' : 'Empty slot · click Replace or Library to upload'}
          </p>
        </div>
      )}
      {/* ── Phase 4 — Device tabs (Desktop/Tablet/Mobile).
             Iter 190j — HIDDEN in inline mode per user request. The editor
             top-bar already exposes a device switcher; showing another one
             inside the image accordion was redundant. Kept for the floating
             (non-inline) popover so public embeds still get responsive
             per-device controls. */}
      {!inline && (
      <div className="flex items-center justify-between px-1 pb-1 mb-1 border-b border-gray-100" data-testid="image-device-tabs">
        <div className="flex gap-0.5 rounded-md p-0.5 bg-gray-100">
          {[
            { v: 'desktop', label: 'Desktop', Icon: Monitor    },
            { v: 'tablet',  label: 'Tablet',  Icon: Tablet     },
            { v: 'mobile',  label: 'Mobile',  Icon: Smartphone },
          ].map(({ v, label, Icon }) => {
            const active = device === v;
            return (
              <button
                key={v}
                type="button"
                onClick={() => setDevice(v)}
                data-testid={`image-device-${v}`}
                title={label}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-all ${
                  active
                    ? (inline ? 'text-white shadow-sm' : 'bg-white shadow-sm text-[#5B21B6]')
                    : (inline ? 'text-white/55 hover:text-white' : 'text-gray-500 hover:text-gray-800')
                }`}
                style={active && inline
                  ? { background: 'linear-gradient(135deg, rgba(139,92,246,0.35), rgba(236,72,153,0.20))', border: '1px solid rgba(167,139,250,0.35)' }
                  : undefined}
              >
                <Icon size={10} />
                {label}
                {responsive && active && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]" title="Custom for this device" />
                )}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={syncFromDesktop}
          data-testid="image-sync-from-desktop"
          title="Copy desktop settings to this device"
          className={`flex items-center gap-1 px-2 py-1 rounded text-[9.5px] uppercase tracking-wider font-bold ${
            inline
              ? 'text-white/55 hover:bg-white/[0.06] hover:text-white'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
          }`}
        >
          <CopyIcon size={10} /> Sync
        </button>
      </div>
      )}

      {/* Top row — primary actions */}
      <div className="flex items-stretch gap-0.5">
        <Btn icon={Upload}      label="Replace"  onClick={() => fileRef.current?.click()} testid="image-toolbar-replace"  inline={inline} />
        <Btn icon={FolderOpen}  label="Library"  onClick={onMediaPick}                    testid="image-toolbar-library"  inline={inline} />
        <Btn icon={Crop}        label="Crop"     onClick={() => setPanel(panel === 'crop' ? null : 'crop')}         testid="image-toolbar-crop"     inline={inline} />
        <Btn icon={Square}      label="Frame"    onClick={() => setPanel(panel === 'frame' ? null : 'frame')}       testid="image-toolbar-frame"    inline={inline} />
        <Btn icon={SettingsIcon} label="Settings" onClick={() => setPanel(panel === 'settings' ? null : 'settings')} testid="image-toolbar-settings" inline={inline} />
        <Btn icon={Trash2}      label="Remove"   onClick={onRemove}                        testid="image-toolbar-remove"  inline={inline} danger />
        {!inline && (
          <>
            <div className="w-px bg-gray-200 mx-1" />
            <button
              onClick={(e) => { e.stopPropagation(); onClose?.(); }}
              className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
              data-testid="image-toolbar-close"
              aria-label="Close"
            >
              <X size={13} />
            </button>
          </>
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files[0])} />

      {/* Iter 190i — Link input (3rd primary option beside Replace + Remove).
          Persisted on `frame.href` so it lives alongside crop/shape data. The
          renderer can consume this to wrap the <img> in an <a href> when set. */}
      {inline && (
        <div className="mt-2 flex items-center gap-2 px-1" data-testid="image-toolbar-link-row">
          <LinkIcon size={12} className="text-white/50 flex-shrink-0" />
          <input
            type="text"
            value={frame?.href || ''}
            onChange={(e) => updateField('href', e.target.value)}
            placeholder="Add link (https://…) — optional"
            className="flex-1 bg-white/[0.06] border border-white/10 rounded-md px-2 py-1.5 text-[11px] text-white placeholder-white/35 focus:outline-none focus:border-[#A78BFA]/50"
            data-testid="image-toolbar-link-input"
          />
          {frame?.href && (
            <button
              type="button"
              onClick={() => updateField('href', '')}
              className="w-6 h-6 grid place-items-center text-white/50 hover:text-white hover:bg-white/10 rounded"
              title="Remove link"
              data-testid="image-toolbar-link-clear"
            >
              <X size={11} />
            </button>
          )}
        </div>
      )}

      {/* ── FRAME PANEL ── */}
      {panel === 'frame' && (
        <div className="mt-2 p-3 border-t border-gray-100" data-testid="image-toolbar-frame-panel">
          <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400 mb-2">Shape Mask</p>
          <div className="grid grid-cols-7 gap-1.5">
            {SHAPE_KEYS.map(key => (
              <button
                key={key}
                type="button"
                onClick={() => updateField('shape', key)}
                title={SHAPES[key].label}
                data-testid={`image-shape-${key}`}
                className={`relative w-12 h-12 rounded-md transition-all flex items-center justify-center ${frame?.shape === key
                  ? 'ring-2 ring-[#8B5CF6] bg-[#8B5CF6]/10'
                  : 'hover:bg-gray-100'}`}
              >
                <span
                  className="w-7 h-7 bg-gradient-to-br from-[#A78BFA] to-[#5B21B6]"
                  style={{ clipPath: SHAPES[key].clipPath === 'none' ? 'none' : SHAPES[key].clipPath, borderRadius: key === 'rectangle' ? 2 : undefined }}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── SETTINGS PANEL ── */}
      {panel === 'settings' && (
        <div className="mt-2 p-3 border-t border-gray-100 space-y-3" data-testid="image-toolbar-settings-panel">
          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400 mb-1.5">Display Mode</p>
            <div className="flex gap-1">
              {DISPLAY_MODES.map(m => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => updateField('displayMode', m.v)}
                  title={m.desc}
                  data-testid={`image-mode-${m.v}`}
                  className={`flex-1 px-2 py-1.5 rounded-md text-[10.5px] uppercase tracking-wider font-bold border transition-all ${
                    (frame?.displayMode || 'fill') === m.v
                      ? 'bg-[#8B5CF6]/10 text-[#5B21B6] border-[#8B5CF6]/30'
                      : 'bg-gray-50 text-gray-500 border-gray-100 hover:border-gray-200 hover:text-gray-700'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <Slider label="Corner Radius" min={0} max={120} value={frame?.radius || 0} onChange={(v) => updateField('radius', v)} testid="image-radius" suffix="px" />
          <Slider label="Opacity"       min={0} max={100} value={Math.round((frame?.opacity ?? 1) * 100)} onChange={(v) => updateField('opacity', v / 100)} testid="image-opacity" suffix="%" />
          <Slider label="Border Width"  min={0} max={20}  value={frame?.borderWidth || 0} onChange={(v) => updateField('borderWidth', v)} testid="image-border-width" suffix="px" />

          <div>
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400 mb-1.5">Border Color</p>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={frame?.borderColor || '#FFFFFF'}
                onChange={(e) => updateField('borderColor', e.target.value)}
                className="w-8 h-8 rounded-md border border-gray-200 cursor-pointer"
                data-testid="image-border-color"
              />
              <span className="text-[11px] text-gray-500 font-mono">{frame?.borderColor || '#FFFFFF'}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── CROP PANEL (Phase 2 — drag-to-reposition + rotation + Apply/Cancel) ── */}
      {panel === 'crop' && (
        <div className="mt-2 p-3 border-t border-gray-100 space-y-3" data-testid="image-toolbar-crop-panel">
          <div className="flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400">Crop & Reposition</p>
            <span className="text-[9px] uppercase tracking-wider text-[#8B5CF6] font-bold">Drag image to move</span>
          </div>
          <Slider label="Zoom"     min={50}  max={300}  value={Math.round((frame?.scale || 1) * 100)} onChange={(v) => updateField('scale', v / 100)} testid="image-zoom"     suffix="%" />
          <Slider label="Rotation" min={-180} max={180} value={frame?.rotation || 0} onChange={(v) => updateField('rotation', v)} testid="image-rotation" suffix="°" />
          <Slider label="Offset X" min={-200} max={200} value={frame?.offsetX || 0} onChange={(v) => updateField('offsetX', v)} testid="image-offset-x" suffix="px" />
          <Slider label="Offset Y" min={-200} max={200} value={frame?.offsetY || 0} onChange={(v) => updateField('offsetY', v)} testid="image-offset-y" suffix="px" />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={resetCrop}
              data-testid="image-crop-reset"
              className="flex-1 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-[11px] font-bold text-gray-700 uppercase tracking-wider"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={cancelCrop}
              data-testid="image-crop-cancel"
              className="flex-1 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-[11px] font-bold text-gray-700 uppercase tracking-wider"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyCrop}
              data-testid="image-crop-apply"
              className="flex-1 px-3 py-2 rounded-md bg-[#8B5CF6] hover:bg-[#7C3AED] text-[11px] font-bold text-white uppercase tracking-wider"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>

    {/* ── DRAG-TO-REPOSITION OVERLAY (only while Crop panel is open) ──
        Sits on top of the image with a faint dashed outline, cursor:move,
        and a pointerdown handler that drags the image inside its frame.
        Re-positioned on scroll/resize so it tracks the underlying wb-image. */}
    {panel === 'crop' && dragRect && (
      <div
        className="fixed z-[155]"
        style={{
          top: dragRect.top,
          left: dragRect.left,
          width: dragRect.width,
          height: dragRect.height,
          cursor: 'move',
          outline: '2px dashed rgba(139,92,246,0.65)',
          outlineOffset: '-2px',
          background: 'rgba(139,92,246,0.04)',
        }}
        data-testid="image-crop-drag-overlay"
        data-wb-overlay="1"
        onPointerDown={onDragStart}
      >
        <div className="absolute top-1.5 left-1.5 px-2 py-0.5 bg-[#8B5CF6] text-white text-[9px] uppercase tracking-wider font-bold rounded">
          Crop Mode — Drag to Move
        </div>
      </div>
    )}
    </>
  );
}

function Slider({ label, min, max, value, onChange, testid, suffix }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-400">{label}</span>
        <span className="text-[10.5px] text-gray-500 font-mono">{value}{suffix}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#8B5CF6]"
        data-testid={testid}
      />
    </div>
  );
}
